import {
  FC,
  forwardRef,
  PropsWithChildren,
  RefObject,
  useEffect,
  useRef,
} from 'react';
import { tabbable } from 'tabbable';
import { classNames } from '@dimjs/utils';
import { useCallbackRef } from '../hooks/use-callback-ref.js';
import { mergeRefs } from '../utils/react-helpers.js';

type FocusTrapProps = {
  className?: string;
  focusToElem?: number;
};

export const FocusTrap: FC<PropsWithChildren<FocusTrapProps>> = forwardRef(
  (props, ref) => {
    const { children, className, focusToElem } = props;
    const root = useRef<null | HTMLDivElement>();
    const anchor: RefObject<any> = useRef(document.activeElement);

    const returnFocus = useCallbackRef(() => {
      // Returns focus to the last focused element prior to trap.
      if (anchor) {
        anchor.current.focus();
      }
    });

    const selectFirstFocusableEl = useCallbackRef((focusToElem: number) => {
      // Try to find focusable elements, if match then focus
      // Up to 6 seconds of load time threshold
      let match = false;
      const end = 60; // Try to find match at least n times
      let i = 0;
      const timer = setInterval(() => {
        if (!match || i > end) {
          try {
            const focusableElements = root.current
              ? tabbable(root.current)
              : [];
            match = focusableElements.length > 0;
            if (match) {
              // Attempt to focus the first el
              focusableElements[focusToElem]?.focus();
            }
          } catch (err) {
            console.warn('focus-trap err', err);
          }
          i = i + 1;
        } else {
          // Clear interval after n attempts
          clearInterval(timer);
        }
      }, 100);
    });

    const trapFocus = useCallbackRef(() => {
      // Focus the container element
      if (root.current) {
        root.current.focus();
        if (typeof focusToElem === 'number') {
          selectFirstFocusableEl(focusToElem);
        }
      }
    });

    useEffect(() => {
      const timeoutId = setTimeout(trapFocus, 20);
      return () => {
        root.current = null;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        returnFocus();
      };
    }, [children, returnFocus, trapFocus]);

    return (
      <div
        ref={mergeRefs(root, ref)}
        tabIndex={-1}
        className={classNames('outline-none focus-trap', className)}
      >
        {children}
      </div>
    );
  }
);
